import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static get targets() {
    return ["select", "tag", "tagTemplate", "tagsContainer", "includeOptions", "excludeOptions", "searchFirmsCheckbox"]
  }

  static get values() {
    return {
      searchFirmsIndustry: String,
    }
  }

  connect() {
    this.selectizeElement = $(this.selectTarget).selectize({
      persist: false,
      create: false,
      copyClassesToDropdown: false,
      onItemAdd: (value, $item) => {
        if (this.hasSearchFirmsCheckboxTarget && value === this.searchFirmsIndustryValue) {
          this.searchFirmsCheckboxTarget.disabled = true
        }
        this.renderItem(value, $item[0].innerText)
      },
      onItemRemove: () => {
        this._refreshPlaceholder()
      },
      onChange: () => {
        this._dispatchChangeEvent()
      },
      render: {
        // We render items outside the select so we have to use this custom render function
        item: (item, escape) => {
          return `<div style="display:none;">${escape(item.text)}</div>`
        },
      },
    })
    this._renderTagsFromSelects()
    Array.from(this.selectTarget.options).forEach((option) => {
      if (option.selected) {
        this.renderItem(option.value, option.innerText)
      }
    })
  }

  disconnect() {
    // Destroy the Selectize element to avoid duplication of the field
    this.selectizeElement[0].selectize.destroy()
  }

  renderItem(value, label, included = true) {
    this._renderTag(label, value, included)
    this._addOption(value, label, included)
    this._refreshPlaceholder()
    this._dispatchChangeEvent()
  }

  toggleSearchFirms() {
    const value = this.searchFirmsIndustryValue
    const selectize = this.selectTarget.selectize
    const label = selectize.options[value].text
    if (this.hasSearchFirmsCheckboxTarget && this.searchFirmsCheckboxTarget.checked) {
      this._addOption(value, label, false)
      selectize.items.push(value)
    } else {
      this._removeOption(value, this.excludeOptionsTarget)
      selectize.items = selectize.items.filter((item) => item !== value)
    }
  }

  removeTag(event) {
    let value = event.params.value
    // Remove tag under the select box
    this.tagTargets.find((tag) => tag.dataset.value === value)?.remove()
    // Sync change to selectize
    this.selectTarget.selectize.removeItem(value)
  }

  tagRemoved(event) {
    let { tagValue, included } = event.detail
    let selectizeItems = this.selectTarget.selectize.items
    const selectObject = included ? this.includeOptionsTarget : this.excludeOptionsTarget
    this._removeOption(tagValue, selectObject)
    if (this.hasSearchFirmsCheckboxTarget && tagValue === this.searchFirmsIndustryValue) {
      this.searchFirmsCheckboxTarget.disabled = false
    }
    this.selectTarget.selectize.removeItem(tagValue)
    this._dispatchChangeEvent()

    // Remove the element from selectizeItems if present
    // this is for items that are rendered via _renderTagsFromSelects()
    if (selectizeItems.indexOf(tagValue) > -1) {
      selectizeItems.splice(selectizeItems.indexOf(tagValue), 1)
    }
  }

  changeTag(event) {
    let { tagValue, included } = event.detail
    const selectObjectToRemoveFrom = included ? this.excludeOptionsTarget : this.includeOptionsTarget
    this._addOption(tagValue, tagValue, included)
    this._removeOption(tagValue, selectObjectToRemoveFrom)
    this._dispatchChangeEvent()
  }

  _renderTagsFromSelects() {
    Array.from(this.includeOptionsTarget.options).forEach((option) => {
      this._renderTag(option.text, option.value, true)
      this.selectTarget.selectize.items.push(option.value)
      if (this.hasSearchFirmsCheckboxTarget && option.value === this.searchFirmsIndustryValue) {
        this.searchFirmsCheckboxTarget.disabled = true
      }
    })
    Array.from(this.excludeOptionsTarget.options).forEach((option) => {
      if (this.hasSearchFirmsCheckboxTarget && option.value === this.searchFirmsIndustryValue) {
        this.searchFirmsCheckboxTarget.checked = true
      } else {
        this._renderTag(option.text, option.value, false)
      }
      this.selectTarget.selectize.items.push(option.value)
    })
  }

  _renderTag(label, value, included) {
    let tagNode = this._generateTagNode(label, value, included)
    this.tagsContainerTarget.appendChild(tagNode.firstElementChild)
  }

  _generateTagNode(label, value, included) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, label)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included)
    let tagNode = document.createElement("div")
    tagNode.innerHTML = tagHtml
    return tagNode
  }

  _addOption(value, label, included) {
    const selectObject = included ? this.includeOptionsTarget : this.excludeOptionsTarget
    if (selectObject.querySelector(`option[value="${value}"]`)) {
      return
    }
    let option = document.createElement("option")
    option.value = value
    option.text = label
    option.selected = true
    selectObject.appendChild(option)
  }

  _removeOption(value, selectObject) {
    let index = 0
    for (let i = 0; i < selectObject.options.length; i++) {
      if (selectObject.options[i].value === value) {
        index = i
        break
      }
    }
    selectObject.options.remove(index)
  }

  // Below function needs to be used whenever we change item state of selectize input so make sure
  // we always show the placeholder
  _refreshPlaceholder() {
    const selectizeInput = this.selectTarget.selectize.$control_input[0]
    selectizeInput.setAttribute("placeholder", this.selectTarget.attributes.placeholder.value)
    selectizeInput.style.width = "inherit"
    selectizeInput.style.height = "100%"
  }

  _dispatchChangeEvent() {
    this.dispatch("change", {})
  }
}
